import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, Loader2, Plus, Trash2, Sparkles } from "lucide-react";

// One-click preset covering the four room-type products the platform expects
// every hostel to eventually offer. Owners can tweak prices / counts after
// dropping the preset in.
const ROOM_TYPE_PRESET = [
  { name: "1 Seater",  price_per_month: 8000, rooms_total: 2, rooms_available: 2, beds_per_room: 1, description: "" },
  { name: "2 Seater",  price_per_month: 6000, rooms_total: 4, rooms_available: 4, beds_per_room: 2, description: "" },
  { name: "3 Seater",  price_per_month: 5000, rooms_total: 4, rooms_available: 4, beds_per_room: 3, description: "" },
  { name: "Dormitory", price_per_month: 3000, rooms_total: 1, rooms_available: 1, beds_per_room: 8, description: "" },
];

export default function HostelEditDialog({ hostel, open, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hostel) return;
    setF({
      name: hostel.name || "",
      address: hostel.address || "",
      city: hostel.city || "",
      price_per_bed: hostel.price_per_bed || 250,
      total_beds: hostel.total_beds || 1,
      available_beds: hostel.available_beds ?? 0,
      description: hostel.description || "",
      owner_phone: hostel.owner_phone || "",
      meal_available: !!hostel.meal_available,
      meal_price: hostel.meal_price || 0,
      meal_description: hostel.meal_description || "",
      ac_available: !!hostel.ac_available,
      ac_price_per_bed: hostel.ac_price_per_bed || 0,
      gender: hostel.gender || "coed",
      room_types: (hostel.room_types || []).map((rt) => ({ ...rt })),
    });
  }, [hostel]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRoomTypes = (updater) => setF((p) => ({ ...p, room_types: updater(p.room_types || []) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        room_types: (f.room_types || [])
          .filter((rt) => rt.name && rt.name.trim())
          .map((rt) => ({
            ...(rt.id ? { id: rt.id } : {}),
            name: rt.name.trim(),
            price_per_month: Number(rt.price_per_month) || 0,
            rooms_total: Number(rt.rooms_total) || 1,
            rooms_available: Number(rt.rooms_available ?? rt.rooms_total) || 0,
            beds_per_room: Number(rt.beds_per_room) || 1,
            description: (rt.description || "").trim(),
            food_included: !!rt.food_included,
          })),
      };
      const { data } = await api.patch(`/hostels/${hostel.id}`, payload);
      toast.success("Hostel updated");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  if (!hostel) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl w-[calc(100%-20px)] rounded-2xl p-0 gap-0 flex flex-col overflow-hidden top-[50px] translate-y-0 max-h-[calc(100dvh-70px)] sm:top-[50%] sm:translate-y-[-50%] sm:max-h-[90vh]"
        data-testid="edit-hostel-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0 text-left">
          <DialogTitle className="font-display text-2xl">Edit hostel</DialogTitle>
          <DialogDescription>Changes appear immediately for students browsing.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">
          <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="eh-name">Hostel name</Label>
              <Input id="eh-name" data-testid="edit-hostel-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="eh-city">City</Label>
              <Input id="eh-city" value={f.city || ""} onChange={(e) => set("city", e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="eh-address">Address</Label>
            <Input id="eh-address" value={f.address || ""} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="eh-price">Price / bed / night (₹)</Label>
              <Input id="eh-price" type="number" min={100} value={f.price_per_bed || ""} onChange={(e) => set("price_per_bed", Number(e.target.value))} />
            </div>
            <div>
              <Label htmlFor="eh-total">Total beds</Label>
              <Input id="eh-total" type="number" min={1} value={f.total_beds || ""} onChange={(e) => set("total_beds", Number(e.target.value))} />
            </div>
            <div>
              <Label htmlFor="eh-avail">Available beds</Label>
              <Input id="eh-avail" type="number" min={0} max={f.total_beds || 999} value={f.available_beds ?? 0} onChange={(e) => set("available_beds", Number(e.target.value))} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="eh-phone">Owner phone (10 digits)</Label>
              <Input id="eh-phone" data-testid="edit-hostel-phone" inputMode="numeric" maxLength={10} value={f.owner_phone || ""} onChange={(e) => set("owner_phone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="9876543210" />
              <p className="text-[10px] text-slate-500 mt-1">Shown to students who book so they can WhatsApp/call for check-in.</p>
            </div>
            <div>
              <Label htmlFor="eh-gender">Accepts</Label>
              <select id="eh-gender" value={f.gender || "coed"} onChange={(e) => set("gender", e.target.value)} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm">
                <option value="boys">Boys only</option>
                <option value="girls">Girls only</option>
                <option value="coed">Co-ed</option>
              </select>
            </div>
          </div>
          <div>
            <Label htmlFor="eh-desc">Description</Label>
            <Textarea id="eh-desc" rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>

          <div className="border-t border-slate-100 pt-3 space-y-3">
            <div className="text-xs tracking-widest uppercase font-bold text-slate-500">Meals & AC</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f.meal_available} onChange={(e) => set("meal_available", e.target.checked)} />
              Meals available
            </label>
            {f.meal_available && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="eh-meal-price">Meal price / bed / day (₹100 – ₹250)</Label>
                  <Input id="eh-meal-price" type="number" min={100} max={250} value={f.meal_price || 0} onChange={(e) => set("meal_price", Number(e.target.value))} />
                </div>
                <div>
                  <Label htmlFor="eh-meal-desc">Meal description</Label>
                  <Input id="eh-meal-desc" value={f.meal_description || ""} onChange={(e) => set("meal_description", e.target.value)} placeholder="Homely veg — breakfast, lunch, dinner" />
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f.ac_available} onChange={(e) => set("ac_available", e.target.checked)} />
              AC available
            </label>
            {f.ac_available && (
              <div>
                <Label htmlFor="eh-ac-price">AC surcharge / bed / night (₹)</Label>
                <Input id="eh-ac-price" type="number" min={0} value={f.ac_price_per_bed || 0} onChange={(e) => set("ac_price_per_bed", Number(e.target.value))} />
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-3 space-y-3" data-testid="edit-room-types">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="text-xs tracking-widest uppercase font-bold text-vermilion-700">Room types</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Monthly-priced options students pick at booking (1 Seater, 2 Seater, Dormitory…).</div>
              </div>
              <div className="flex items-center gap-2">
                {(f.room_types || []).length === 0 && (
                  <Button
                    type="button"
                    size="sm"
                    data-testid="edit-hostel-add-preset-room-types"
                    onClick={() => setRoomTypes(() => ROOM_TYPE_PRESET.map((rt) => ({ ...rt })))}
                    className="rounded-full gap-1 h-8 px-3 bg-vermilion-600 hover:bg-vermilion-700 text-white"
                  >
                    <Sparkles size={12} /> Add all 4 defaults
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="edit-hostel-add-room-type"
                  onClick={() => setRoomTypes((prev) => [...prev, { name: "", price_per_month: 6000, rooms_total: 1, rooms_available: 1, beds_per_room: 1, description: "" }])}
                  className="rounded-full gap-1 h-8 px-3"
                >
                  <Plus size={12} /> Add
                </Button>
              </div>
            </div>

            {(f.room_types || []).length === 0 && (
              <div className="rounded-xl bg-marigold-50 border border-marigold-300 px-3 py-2.5 text-[12px] text-vermilion-800">
                No room types yet. Tap <b>Add all 4 defaults</b> above to drop in 1 Seater, 2 Seater, 3 Seater, and Dormitory presets — then tweak prices and counts.
              </div>
            )}

            {(f.room_types || []).map((rt, i) => (
              <div key={i} data-testid={`edit-room-type-row-${i}`} className="bg-white border border-slate-200 rounded-xl p-3 grid gap-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Name</Label>
                    <Input data-testid={`edit-room-type-name-${i}`} placeholder="1 Seater" value={rt.name || ""} onChange={(e) => setRoomTypes((prev) => prev.map((row, idx) => idx === i ? { ...row, name: e.target.value } : row))} className="mt-1 h-9" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Price / month ₹</Label>
                    <Input data-testid={`edit-room-type-price-${i}`} type="number" min={0} value={rt.price_per_month ?? 0} onChange={(e) => setRoomTypes((prev) => prev.map((row, idx) => idx === i ? { ...row, price_per_month: e.target.value } : row))} className="mt-1 h-9" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Rooms total</Label>
                    <Input data-testid={`edit-room-type-total-${i}`} type="number" min={1} value={rt.rooms_total ?? 1} onChange={(e) => setRoomTypes((prev) => prev.map((row, idx) => idx === i ? { ...row, rooms_total: e.target.value, rooms_available: Math.min(Number(row.rooms_available ?? row.rooms_total) || 0, Number(e.target.value) || 0) } : row))} className="mt-1 h-9" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Available</Label>
                    <Input data-testid={`edit-room-type-available-${i}`} type="number" min={0} max={rt.rooms_total ?? 999} value={rt.rooms_available ?? 0} onChange={(e) => setRoomTypes((prev) => prev.map((row, idx) => idx === i ? { ...row, rooms_available: e.target.value } : row))} className="mt-1 h-9" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-600">Beds / room</Label>
                    <Input data-testid={`edit-room-type-beds-${i}`} type="number" min={1} value={rt.beds_per_room ?? 1} onChange={(e) => setRoomTypes((prev) => prev.map((row, idx) => idx === i ? { ...row, beds_per_room: e.target.value } : row))} className="mt-1 h-9" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 bg-emerald-50/60 border border-emerald-200 rounded-lg px-2.5 py-2">
                  <Checkbox data-testid={`edit-room-type-food-${i}`} checked={!!rt.food_included} onCheckedChange={(val) => setRoomTypes((prev) => prev.map((row, idx) => idx === i ? { ...row, food_included: !!val } : row))} />
                  Price includes monthly food <span className="text-[10px] text-slate-500 font-normal">— students won't be charged for meals</span>
                </label>
                <div className="flex justify-end">
                  <button type="button" data-testid={`edit-room-type-remove-${i}`} onClick={() => setRoomTypes((prev) => prev.filter((_, idx) => idx !== i))} className="text-xs font-semibold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
        <DialogFooter className="px-6 py-3 border-t border-slate-100 flex-shrink-0 bg-white gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-full">Cancel</Button>
          <Button data-testid="edit-hostel-save" onClick={save} disabled={saving} className="rounded-full bg-orange-800 hover:bg-orange-900 text-white gap-1">
            {saving ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <><Save size={14} /> Save changes</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
